import { Injectable } from '@angular/core';
import { AbstractOrganizationService } from './abstractOrganizationService';
import { HttpClient, HttpContext } from '@angular/common/http';
import { AuthService } from './auth.service';
import { CdnStatus, Hsts, Policy, RuleAction, Site } from '../models/site';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Audit } from '../models/audit';
import _ from 'lodash';
import { getPageParams, PageRequest, SpringPage } from '@shared/page';
import { TlsOptions } from './organization-tls-options.service';
import { SILENT_ERRORS } from '@shared/services/loader-interceptor.service';
import { SiteMode } from '@shared/services/hostMode.service';
import { UrlScheme } from './site.service';
import { ClusterRef, ContractRef } from '../models/refs';

import { compareCidr } from '@shared/utils/ip-address';
import {
  CustomPagesProfileModel,
} from '@shared/services/custom-page-profiles/organization-custom-pages-profile.service';
import { Page } from '@shared/utils/legacy-api';
import { ClusterUidAndName } from '@shared/models/cluster';
import { BrainParameter, BrainTunableType, Visibility } from '@shared/services/brain-parameters.service';

@Injectable({
  providedIn: 'root',
})
export class OrganizationSiteService extends AbstractOrganizationService {
  constructor(
    protected readonly http: HttpClient,
    auth: AuthService,
  ) {
    super(auth);
  }

  public getSite(siteRef: SiteRef): Observable<SiteDetail> {
    return this.http.get<SiteDetail>(this.siteUrl(siteRef)).pipe(this.cleanupSite());
  }

  public getSites(index: number, size: number, data: any) {
    return this.http.post<Page<GetSites>>(this.sitesUrl() + '/get', { index, size, data });
  }

  public getAdminClusterSites(index: number, size: number, data: any) {
    return this.http.post<Page<GetSites>>(this.sitesUrl() + '/getByCluster', { index, size, data });
  }

  private cleanupSite() {
    return map((site: SiteDetail) => {
      site.rules = site.rules.map((rule) => this.conformRule(rule));
      site.rewriteRules.forEach((rewriteRule) => delete rewriteRule['priority']);
      return site;
    });
  }

  private conformRule(rule: Rule) {
    if (rule.ipExceptions) {
      rule.ipExceptions.sort(compareCidr);
    }
    return rule;
  }

  public createSite(request: CreateSiteRequest): Observable<SiteDetail> {
    return this.http.post<SiteDetail>(this.sitesUrl(), request).pipe(this.cleanupSite());
  }

  public patchSite(siteRef: SiteRef, request: UpdateSiteRequest): Observable<SiteDetail> {
    return this.http.patch<SiteDetail>(this.siteUrl(siteRef), request).pipe(this.cleanupSite());
  }

  deleteSite(siteRef: SiteRef) {
    return this.http.delete<void>(this.siteUrl(siteRef));
  }

  refreshStatus(siteRef: SiteRef) {
    return this.http.post(this.siteUrl(siteRef) + '/refresh-status', {});
  }

  purgeCache(siteRef: SiteRef, purgeRegexp: string) {
    return this.http.post(this.siteUrl(siteRef) + '/purge-cache', { path: purgeRegexp });
  }

  // Tags

  public getTags(siteRef: SiteRef): Observable<string[]> {
    return this.http.get<{ tags: string[] }>(this.siteUrl(siteRef) + '/tags').pipe(map((body) => body.tags));
  }

  // Certificates

  getCertificates(siteRef: SiteRef): Observable<Certificate[]> {
    return this.http.get<Certificate[]>(this.siteUrl(siteRef) + '/certificates');
  }

  addCsr(siteRef: SiteRef, csr: Csr): Observable<Certificate> {
    return this.http.post<Certificate>(this.siteUrl(siteRef) + '/certificates/csr', csr);
  }

  uploadP12(siteRef: SiteRef, uploadP12Request: UploadP12Request): Observable<Certificate> {
    const formData = new FormData();
    formData.append('p12Data', uploadP12Request.p12Data, uploadP12Request.p12Data.name);
    formData.append('makeActive', uploadP12Request.makeActive ? 'true' : 'false');
    if (uploadP12Request.p12Password) formData.append('p12Password', uploadP12Request.p12Password);
    return this.http.post<Certificate>(this.siteUrl(siteRef) + '/certificates/p12', formData, {
      headers: { formData: 'true' },
    });
  }

  updateCertificate(siteRef: SiteRef, certificateId: number, request: CertificateUpdateRequest) {
    return this.http.patch<Certificate>(this.siteUrl(siteRef) + `/certificates/${certificateId}`, request);
  }

  deleteCertificate(siteRef: SiteRef, certificateId: number) {
    return this.http.delete(this.siteUrl(siteRef) + `/certificates/${certificateId}`);
  }

  // TLS options

  getAvailableTlsOptions(siteRef: SiteRef, pageRequest: PageRequest) {
    return this.http.get<SpringPage<TlsOptions>>(this.siteUrl(siteRef) + '/available-tls-options', {
      params: getPageParams(pageRequest),
    });
  }

  // IP exceptions

  getIpExceptions(siteRef: SiteRef): Observable<IpWithComment[]> {
    return this.http.get<IpWithComment[]>(this.siteUrl(siteRef) + '/ip-exceptions');
  }

  setIpExceptions(siteRef: SiteRef, trustedIps: IpWithComment[]): Observable<IpWithComment[]> {
    return this.http.put<IpWithComment[]>(this.siteUrl(siteRef) + `/ip-exceptions`, trustedIps);
  }

  // Blacklisted IPs

  getBlacklistedIps(siteRef: SiteRef): Observable<IpWithComment[]> {
    return this.http.get<IpWithComment[]>(this.siteUrl(siteRef) + '/blacklisted-ips');
  }

  setBlacklistedIps(siteRef: SiteRef, trustedIps: IpWithComment[]): Observable<IpWithComment[]> {
    return this.http.put<IpWithComment[]>(this.siteUrl(siteRef) + `/blacklisted-ips`, trustedIps);
  }

  // Trusted Ips

  getTrustedIps(siteRef: SiteRef): Observable<any[]> {
    return this.http.get<string[]>(this.siteUrl(siteRef) + '/trusted-ips');
  }

  setTrustedIps(siteRef: SiteRef, trustedIps: IpWithComment[]): Observable<IpWithComment[]> {
    return this.http.put<IpWithComment[]>(this.siteUrl(siteRef) + `/trusted-ips`, trustedIps);
  }

  // URL exceptions

  getUrlExceptions(siteRef: SiteRef): Observable<UrlException[]> {
    return this.http.get<UrlException[]>(this.siteUrl(siteRef) + '/url-exceptions');
  }

  addUrlException(siteRef: SiteRef, urlException: Omit<UrlException, 'id'>): Observable<UrlException> {
    return this.http.post<UrlException>(this.siteUrl(siteRef) + '/url-exceptions', urlException);
  }

  updateUrlException(siteRef: SiteRef, urlException: UrlException): Observable<UrlException> {
    return this.http.put<UrlException>(
      this.siteUrl(siteRef) + `/url-exceptions/${urlException.id}`,
      _.omit(urlException, 'id'),
    );
  }

  deleteUrlException(siteRef: SiteRef, urlExceptionId: UrlException['id']) {
    return this.http.delete(this.siteUrl(siteRef) + `/url-exceptions/${urlExceptionId}`);
  }

  // Rules

  getRules(siteRef: SiteRef): Observable<(Rule & Audit)[]> {
    return this.http
      .get<(Rule & Audit)[]>(this.siteUrl(siteRef) + '/rules')
      .pipe(map((rules) => rules.map((rule) => this.conformRule(rule) as Rule & Audit)));
  }

  // Rewrite rules

  getRewriteRules(siteRef: SiteRef) {
    return this.http.get<(RewriteRule & Audit)[]>(this.siteUrl(siteRef) + '/rewrite-rules').pipe(
      map((rewriteRules) =>
        rewriteRules.map((rewriteRule) => {
          delete rewriteRule['priority'];
          return rewriteRule;
        }),
      ),
    );
  }

  createRewriteRule(siteRef: SiteRef, rule: Omit<RewriteRule, 'id'>) {
    return this.http.post<RewriteRule>(this.siteUrl(siteRef) + `/rewrite-rules`, rule);
  }

  updateRewriteRule(siteRef: SiteRef, rule: RewriteRule) {
    return this.http.put<RewriteRule>(this.siteUrl(siteRef) + `/rewrite-rules/${rule.id}`, _.omit(rule, 'id'));
  }

  deleteRewriteRule(siteRef: SiteRef, ruleId: number) {
    return this.http.delete(this.siteUrl(siteRef) + `/rewrite-rules/${ruleId}`);
  }

  // Brain overrides

  getBrainOverrides(siteRef: SiteRef) {
    return this.http.get<BrainOverrides>(this.siteUrl(siteRef) + '/brain-overrides');
  }

  setBrainOverrides(siteRef: SiteRef, maxVisibility: Visibility, overrides: BrainOverrides) {
    return this.http.put(this.siteUrl(siteRef) + `/brain-overrides/${maxVisibility}`, overrides);
  }

  getBrainParameters(siteRef: SiteRef, maxVisibility: Visibility) {
    return this.http.get<SiteBrainParameter[]>(this.siteUrl(siteRef) + `/brain-parameters/${maxVisibility}`);
  }

  // Share

  share(siteRef: SiteRef, recipient, role) {
    return this.http.put<SiteShare>(this.siteUrl(siteRef) + `/shares/${recipient}`, { role });
  }

  deleteShare(siteRef: SiteRef, recipient) {
    return this.http.delete<SiteShare>(this.siteUrl(siteRef) + `/shares/${recipient}`);
  }

  private siteUrl(siteRef: SiteRef) {
    return this.sitesUrl() + '/' + siteRef;
  }

  private sitesUrl() {
    return this.url('/sites');
  }
}

type CertificateUpdateRequest = {
  active?: boolean;
  fullChainCert?: string;
};

export type CreateSiteRequest = {
  contract?: ContractRef;
  cluster: ClusterRef;

  mode?: SiteMode;

  domainName: string;
  originServer: string;
  originScheme?: UrlScheme;
  originPort?: number;
  originMtlsEnabled?: boolean;
  originSkipCertVerify?: boolean;

  forceHttps?: boolean;
  hsts?: Hsts;
  tlsOptions?: { uid: string };
  passTlsClientCert?: PassTlsClientCert;

  removeXForwarded?: boolean;
  passthroughMode?: boolean;
  auditMode?: boolean;
  logExportEnabled?: boolean;

  cacheEnabled?: boolean;

  ddosProtection?: boolean;

  urlException?: Omit<UrlException, 'id'>[];
  ipExceptions?: IpWithComment[];
  trustedIps?: IpWithComment[];
  blacklistedIps?: IpWithComment[];

  blacklistedCountries?: string[];
  rewriteRules?: Omit<RewriteRule, 'id' | 'priority'>[];
  rules?: Omit<Rule, 'id' | 'priority'>[];

  tags?: string[];
};

export type UpdateSiteRequest = Partial<
  Omit<CreateSiteRequest, 'cluster' | 'domainName'> & {
    cdn?: string;
    letsEncryptCertificateFallbackEnabled?: boolean;
  }
>;

export interface SiteDetail extends Site {
  certificates: {
    active: boolean;
    createdAt: number | string;
    updatedAt?: number | string;
    error?: string;
    type: TypeCertificate;
  }[];
  urlExceptions: UrlException[];
  ipExceptions: IpWithComment[];
  blacklistedIps: IpWithComment[];
  trustedIps: IpWithComment[];
  blacklistedCountries: string[];
  rewriteRules: RewriteRule[];
  rules: Rule[];

  shares: SiteShare[];
}

export type TypeCertificate = 'LE' | 'CE';

export type SiteRef = number | string;

export type SiteShare = {
  role: string;
  recipient: {
    code: string;
    name: string;
  };
};

export type Certificate = {
  id: number;
  cn: string;
  type: TypeCertificate;
  csr: string;
  fullChainCert: string;
  expiredAt: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  error: string;
};

export type UrlException = {
  id: number;
  path: string;
  comment?: string;
};

export type IpWithComment = {
  ip: string;
  comment?: string;
};

export type IpException = IpWithComment & {
  id: number;
};

export type RewriteRule = {
  id: number;
  active: boolean;
  rewriteSource: string;
  rewriteDestination: string;
  comment?: string;
};

export interface Rule {
  id?: number;
  active: boolean;
  action: RuleAction;
  comment?: string;
  paths: Array<string>;
  cache?: boolean;
  defaultPolicy: Policy;
  ipExceptions?: Array<string>;
  challenge: ChallengeType;
  customPagesProfile?: CustomPagesProfileModel;
}

export const ChallengeTypes = ['disabled', 'managed', 'forced'] as const;
export type ChallengeType = (typeof ChallengeTypes)[number];

export type BrainOverrides = Record<string, number>;

export type UploadP12Request = {
  p12Data: File;
  p12Password?: string;
  makeActive: boolean;
};

export type Csr = {
  commonName: string;
  san?: string[];
  countryName?: string;
  stateOrProvinceName?: string;
  localityName?: string;
  organizationName?: string;
  organizationalUnit?: string;
};

export const PassTlsClientCerts = ['all', 'cert', 'info', 'none'] as const;
export type PassTlsClientCert = (typeof PassTlsClientCerts)[number];

export interface GetSites {
  domainName: string;
  mode: SiteMode;
  status: string;

  cluster: ClusterUidAndName;

  originServer: string;
  originScheme: string;
  originPort: number;

  auditMode: boolean;
  passthroughMode: boolean;
  ddosProtection: boolean;
  managed: boolean;

  cdn: string;
  cdnActivationInProgress: boolean;
  isACertificateInError: boolean;
  errorMessage: string;
  cdnStatus: CdnStatus;

  tags: { tag: string }[];
  accessRights: string[];
  shares: SiteShare[];

  nbShares: number;

  owner: {
    code: string;
    name: string;
  };

  createdAt: number;
}

export type SiteBrainParameter = BrainParameter & {
  defaultValue?: number;
};
